import React, { Suspense, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import { isMobile } from 'react-device-detect';

import * as THREE from 'three';

// import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

import {
    Canvas,
    useLoader,
    useThree,
} from '@react-three/fiber';

import {
    Box,
    Stats,
    useProgress,
    Html,
} from '@react-three/drei';

import {
    useTheme,
    IconButton,
    Menu,
    MenuItem,
    CircularProgress,
    Button
} from '@mui/material';
import {
    Article,
    CameraAlt,
    Dashboard,
    MoreVert,
    Rectangle,
    PublishedWithChanges
} from '@mui/icons-material';

import CubesIcon from '../../../icons/CubesIcon';
import CubeIcon from '../../../icons/CubeIcon';

import apiClient from '../../../../services/api';

import { LoadingScreen, CustomLoadingScreen } from '../../../partials/LoadingScreen';
import CustomIconButton from '../../../partials/CustomIconButton';

import store from '../../../../store';
import { toggleMessageOpen } from '../../../../store/actions/Message';

import chunkUpload from '../../../../utils/ChunkUpload';
import { calcCameraDistance } from '../../../../utils/Utils';
import { setElements, setMesh, setMeshId, setOptimizedView, setProjectElementId } from '../../../../store/shortcuts/Project';
import { setSelectedProject, setSideBarIsOpened, setUsdzUrl } from '../../../../store/actions/Project';

import Lights from '../partials/Lights';
import Background from '../../../partials/project/view/Background';
import Floor from '../../../partials/project/view/Floor';
import Cameras from '../../../partials/project/view/Cameras';
import Controls from '../../../partials/project/view/Controls';
// import { IS_DEV } from '../../../../isdev';
import StyledIconButton from '../../../partials/easyFloor/StyledIconButton';

const SHADOWS = false;
const CULLING = false;

const STATS = false;

const CAMERA_DISTANCE = 100;

/**
 * Get element position
 * If the element is a 3D object then return position stored in DB
 * Otherwise if the element has to be built by a rectangle then half of the dimension has to be added or removed to the position
 * 
 * @param {object} element 
 * @param {object} position
 * @returns 
 */
const getElementPosition = (element, position) => {
    const x = Number(position.positionX);
    const y = Number(position.positionY);
    const z = Number(position.positionZ);

    if (element.object) return [x, y, z];

    return [
        x + Math.round((Math.abs(element.width) / 2)),
        y + Math.round((Math.abs(element.height) / 2)),
        z - Math.round((Math.abs(element.depth) / 2)),
    ];
};

/**
 * Get element size
 * 
 * @param {object} element 
 * @returns 
 */
const getElementSize = (element) => {
    return [element.width, element.height, element.depth];
};

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;

    return { width, height };
}

/**
 * Floating buttons component
 * 
 * @param {} param0 
 * @returns
 */
const FloatingButtons = ({
    viewType,
    setViewType,
    isOptimizedView,
    handleOptimizedViewChange,
    id,
    model,
    usdzUrl
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const sideBarIsOpened = useSelector((state) => state.project.sideBarIsOpened);
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    
    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const style={ position: 'absolute', top: '50%', right: '20px', zIndex: 10, transform: 'translateY(-50%)' }

    return (
        <div style={style}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <StyledIconButton
                    color="primary"
                    title={t('Change View')}
                    isActive={false}
                    onClick={() => {
                        if(viewType == '3D') {
                            setViewType('2D');
                        } else {
                            setViewType('3D');
                        }
                    }}
                >
                    {viewType != '3D' ? <CubeIcon /> : <CubesIcon />}
                </StyledIconButton>
                <StyledIconButton
                    color="primary"
                    isActive={false}
                    onClick={handleOptimizedViewChange}
                >
                    {isOptimizedView ? <Rectangle /> : <Dashboard />}
                </StyledIconButton>

                <StyledIconButton
                    color="primary"
                    title={t('Show report')}
                    isActive={sideBarIsOpened}
                    onClick={() => { store.dispatch(setSideBarIsOpened(sideBarIsOpened ? false : true)); }}
                >
                    <Article />
                </StyledIconButton>
            </div>
        </div>
    );
};

/**
 * Set the background by the selected theme
 * 
 * @returns 
 */
const ThemedBackground = () => {
    const theme = useTheme();
    return <Background color={theme.palette.background.default} />;
};

/**
 * Grid helper by the selected theme
 * @returns 
 */
const ThemedFloor = () => {
    const theme = useTheme();
    return <Floor color={theme.palette.border.grid} />;
};

/**
 * Calculates the center of the project
 * @param {*} param0 
 * @returns 
 */
const calcProjectCenter = ({ w = 100, h = 100, d = 100 }) => {
    const x = w / 2;
    const y = h / 2;
    const z = d / 2;

    return { x, y, z };
};

/**
 * Calculates the center of the project based on their project elements
 * 
 * @param {*} data 
 * @param {*} model 
 * @returns 
 */
const calcProjectElementsCenter = (data, model) => {
    const bBox = {
        min: { x: 0, y: 0, z: 0 },
        max: { x: 0, y: 0, z: 0 },
    };
    
    if (model) {
        return {
            x: Number(data.project.width) / 2,
            y: Number(data.project.height) / 2,
            z: Number(data.project.depth) / 2,
            w: Number(data.project.width),
            h: Number(data.project.height),
            d: Number(data.project.depth),
        };
    }
    
    const elements = data.project_elements;

    elements.forEach((element) => {
        if (element.positions) element.positions.forEach((position) => {
            const { positionX, positionY, positionZ } = position;

            const px = Number(positionX);
            const py = Number(positionY);
            const pz = Number(positionZ);

            // TODO: element sizes
            bBox.min.x = Math.min(px, bBox.min.x);
            bBox.min.y = Math.min(py, bBox.min.y);
            bBox.min.z = Math.min(pz, bBox.min.z);
            bBox.max.x = Math.max(px, bBox.max.x);
            bBox.max.y = Math.max(py, bBox.max.y);
            bBox.max.z = Math.max(pz, bBox.max.z);
        });
    });

    const w = bBox.max.x - bBox.min.x;
    const h = bBox.max.y - bBox.min.y;
    const d = bBox.max.z - bBox.min.z;

    const x = ((bBox.min.x + bBox.max.x) / 2);
    const y = ((bBox.min.y + bBox.max.y) / 2);
    const z = ((bBox.min.z + bBox.max.z) / 2);

    return { x, y, z, w, h, d };
};

/**
 * Defining default materials
 */
const materialLambertWhite = <meshLambertMaterial attach="material" color="white" depthWrite="false" />;
const materialLambertTransparent = <meshLambertMaterial attach="material" color="white" transparent opacity={0.5} depthWrite="false" />;
const materialLambertWhite0 = <meshLambertMaterial key="0" attach="material-0" color="white" depthWrite="false" />;
const materialLambertWhite1 = <meshLambertMaterial key="1" attach="material-1" color="white" depthWrite="false" />;
const materialLambertWhite2 = <meshLambertMaterial key="2" attach="material-2" color="white" depthWrite="false" />;
const materialLambertWhite3 = <meshLambertMaterial key="3" attach="material-3" color="white" depthWrite="false" />;
const materialLambertWhite5 = <meshLambertMaterial key="5" attach="material-5" color="white" depthWrite="false" />;

/**
 * Product component for fallback if the product image fails to load
 * 
 * @param {*} param0 
 * @returns 
 */
const ProductBox = ({ pos, size }) => (
    <Box position={pos} args={size} castShadow={SHADOWS} receiveShadow={SHADOWS}>
        {materialLambertWhite}
    </Box>
);

/**
 * Product component
 * 
 * @param {*} param0 
 * @returns 
 */
const Product = ({ element, position }) => {
    const size = getElementSize(element);
    const pos = getElementPosition(element, position);

    if (element.image) {
        return (
            <Suspense fallback={<ProductBox pos={pos} size={size} />}>
                <ProductWithImage image={element.image} size={size} position={pos} />
            </Suspense>
        );
    }

    return <ProductBox pos={pos} size={size} />;
};

/**
 * Product component which has front image
 * 
 * @param {*} param0 
 * @returns 
 */
const ProductWithImage = ({ image, size, position }) => {
    const { gl } = useThree();

    const map = useLoader(THREE.TextureLoader, image);

    useEffect(() => {
        gl.initTexture(map);
    }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

    const materials = map ? [
        materialLambertWhite0, materialLambertWhite1,
        materialLambertWhite2, materialLambertWhite3,
        <meshLambertMaterial key="4" attach="material-4" map={map} depthWrite="false" />,
        materialLambertWhite5,
    ] : materialLambertWhite;

    return (
        <mesh position={position} castShadow={SHADOWS} receiveShadow={SHADOWS} frustumCulled={CULLING}>
            <boxGeometry args={size} />
            {materials}
        </mesh> 
    );
};

/**
 * Fixture component
 * @param {*} param0 
 * @returns 
 */
const Fixture = ({ element, position }) => {
    const pos = getElementPosition(element, position);
    const size = getElementSize(element);

    return (
        <Box position={pos} args={size} castShadow={SHADOWS} receiveShadow={SHADOWS}>
            {materialLambertTransparent}
        </Box>
    );
};

/**
 * Instanced project elements
 * 
 * @param {*} param0 
 * @returns 
 */
const AutoInstanced = ({ element }) => {
    if (element.object) {
        // return <InstancedObject element={element} />
    }

    if (element.image) {
        return (
            <Suspense fallback={<InstancedBox element={element} />}>
                <InstancedBoxWithImage element={element} />
            </Suspense>
        );
    }

    return <InstancedBox element={element} />;
};

/**
 * Instanced box
 * 
 * @param {*} param0 
 * @returns 
 */
const InstancedBox = ({ element, temp = new THREE.Object3D() }) => {
    const size = getElementSize(element);
    const positions = element.positions.map((position) => {
        return getElementPosition(element, position);
    });
    const count = positions.length;

    const ref = useRef();
    useEffect(() => {
        positions.forEach((pos, i) => {
            temp.position.set(pos[0], pos[1], pos[2]);
            temp.updateMatrix();
            ref.current.setMatrixAt(i, temp.matrix);
        });
        ref.current.instanceMatrix.needsUpdate = true;
        ref.current.computeBoundingBox();
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    return (
        <instancedMesh ref={ref} args={[null, null, count]} castShadow={SHADOWS} receiveShadow={SHADOWS} frustumCulled={CULLING}>
            <boxGeometry args={size} />
            {materialLambertWhite}
        </instancedMesh>
    );
};

/**
 * Instanced box with front image
 * 
 * @param {*} param0 
 * @returns 
 */
const InstancedBoxWithImage = ({ element, temp = new THREE.Object3D() }) => {
    const { gl } = useThree();

    const size = getElementSize(element);
    const positions = element.positions.map((position) => {
        return getElementPosition(element, position);
    });
    const count = positions.length;

    const map = useLoader(THREE.TextureLoader, element.image);

    useEffect(() => {
        gl.initTexture(map);
    }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

    const ref = useRef();
    useEffect(() => {
        positions.forEach((pos, i) => {
            temp.position.set(pos[0], pos[1], pos[2]);
            temp.updateMatrix();
            ref.current.setMatrixAt(i, temp.matrix);
        });
        ref.current.instanceMatrix.needsUpdate = true;
        ref.current.computeBoundingBox();
    }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

    const materials = map ? [
        materialLambertWhite0, materialLambertWhite1,
        materialLambertWhite2, materialLambertWhite3,
        <meshLambertMaterial key="4" attach="material-4" map={map} depthWrite="false" />,
        materialLambertWhite5,
    ] : materialLambertWhite;

    return (
        <instancedMesh ref={ref} args={[null, null, count]} castShadow={SHADOWS} receiveShadow={SHADOWS} frustumCulled={CULLING} >
            <boxGeometry args={size} />
            {materials}
        </instancedMesh>
    );
};

/**
 * Project component renders Products and Fixtures of the project
 * 
 * @param {*} param0 
 * @returns 
 */
const Project = ({ elements }) => {
    return null;
    return elements ? elements.map(
        (element, index) => {
            const count = element.positions.length;
            if (element.object) {
                if (false && count > 1) {
                    // TODO: instanced
                    return <InstancedObject key={`i${element.object_id}_${index}`} element={element} />; 
                } else {
                    return (
                        <Suspense key={`g${element.object_id}_${index}`} fallback={<ObjectFallback element={element} />}>
                            <ObjectGroup element={element} />
                        </Suspense>
                    );
                }
            }
            if (count > 1) {
                return (
                    <AutoInstanced key={`a${element.object_id}_${index}`} element={element} />
                );
            } else {
                return element.positions.map((position, index) => (
                    element.is_product
                        ? <Product key={`p${element.object_id}_${index}`} element={element} position={position} />
                        : <Fixture key={`f${element.object_id}_${index}`} element={element} position={position} />
                ));
            }
        },
    ) : null;
};

const materialPoints = <pointsMaterial attach="material" color="white" size={2} sizeAttenuation={false} />;

/**
 * Object fallback component which loads when object is failed to load
 * 
 * @param {*} param0 
 * @returns 
 */
const ObjectFallback = ({ element }) => {
    const positions = new Float32Array(element.positions.map(
        (position) => getElementPosition(element, position),
    ).flat());

    return (
        <points>
            <bufferGeometry attach="geometry">
                <bufferAttribute
                    itemSize={3}
                    array={positions}
                    count={positions.length / 3}
                    usage={THREE.StaticDrawUsage}
                    attach="attributes-position"
                />
            </bufferGeometry>
            {materialPoints}
        </points>
    );
};

/**
 * useMemo hook to have possibility to load same object multiple times
 */
const ObjectMemo = memo(({ scene, position }) => {
    const copiedScene = useMemo(() => scene.clone(), [scene]);
    return <group><primitive object={copiedScene} position={position} /></group>;
});

/**
 * Object group component
 * 
 * @param {*} param0 
 * @returns 
 */
const ObjectGroup = ({ element }) => {
    const { object, positions } = element;
    const gltf = useLoader(GLTFLoader, object);

    return (
        <group dispose={null} castShadow={SHADOWS} receiveShadow={SHADOWS} frustumCulled={CULLING}>
            {positions.map((position, index) => {
                const pos = getElementPosition(element, position);
                return <ObjectMemo key={index} scene={gltf.scene} position={pos} />;
            })}
        </group>
    );
};

// WIP: object decomposition

const backupGeometry = <boxGeometry args={[10, 10, 10]} />;
const backupMaterial = <meshLambertMaterial color="green" />;

/**
 * Instanced object
 * 
 * @param {*} param0 
 * @returns 
 */
const InstancedObject = ({ element, temp = new THREE.Object3D() }) => {
    const positions = element.positions.map((position) => {
        return getElementPosition(element, position);
    });
    const count = positions.length;

    //const url = element.object;
    //const object = useLoader(GLTFLoader, url);

    let geometry = backupGeometry;
    let material = backupMaterial;

    /* // test with first mesh found
    if (Object.keys(object.nodes).length > 0) {
        Object.keys(object.nodes).forEach((k) => {
            if (object.nodes[k].type === 'Mesh') {
                console.log(object.nodes[k]);
                geometry = object.nodes[k];
                material = <meshStandardMaterial attach="material" {...object.nodes[k].material} />
            }
        })
    }
    // */

    const ref = useRef();

    useEffect(() => {
        positions.forEach((pos, i) => {
            temp.position.set(pos[0], pos[1], pos[2]);
            temp.updateMatrix();
            ref.current.setMatrixAt(i, temp.matrix);
        });
        ref.current.instanceMatrix.needsUpdate = true;
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    return (
        <instancedMesh ref={ref} args={[null, null, count]} >
            {geometry}
            {material}
        </instancedMesh>
    );

    //* WIP: drei version
    /*
    return (
        <Instances limit={count} castShadow={SHADOWS} receiveShadow={SHADOWS}>
            {!object.scene ? backupGeometry : (
                <Merged meshes={object.nodes} />
            )}
            {positions.map((pos, index) => (<Instance key={index} position={pos}/>))}
        </Instances>
    );
    */
};

/*
function isIdentityMatrix(matrix_data) {
    return (JSON.stringify(matrix_data) === JSON.stringify([1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1]) ? true : false);
}
*/

/**
 * Loading component
 * @returns 
 */
function Loading() {
    const { progress } = useProgress();

    return (
        <Html left>
            <div style={{ width: '1028px' }}>
                <LoadingScreen
                    text={progress.toFixed(2)+'% loaded'}
                    value={progress}
                    variant='determinate'
                />
            </div>
        </Html>
    );
}

const THREE_PATH = `https://unpkg.com/three@0.${THREE.REVISION}.x`;

const hoveredColor = 0x90EE90;
const selectedColor = 0x00FF00;

const getNodesColors = (gltf) => {
    const colors = [];
    Object.keys(gltf.nodes).forEach(key => {
        if (gltf.nodes[key].isMesh) {
            colors[key] = new THREE.Color(
                gltf.nodes[key].material.color.r,
                gltf.nodes[key].material.color.g,
                gltf.nodes[key].material.color.b,
            );
        }
    });
    return colors;
};

const findNodeByUserData = (gltf, key, value) => {
    let result = [];
    const { nodes } = gltf;
    if (nodes) Object.keys(nodes).forEach(k => {
        if (nodes[k].isMesh) {
            const { userData } = nodes[k];
            if (value && userData && userData[key] == value) {
                result.push(nodes[k]);
            }
        }
    });
    return result;
};

const setNodesColors = (gltf, colors) => {
    Object.keys(gltf.nodes).forEach(key => {
        if (gltf.nodes[key].isMesh) {
            gltf.nodes[key].material.color.set(colors[key]);
        }
    });
};

/**
 * Render GLTF model
 * Initialize click and hover listeners
 * useFrame hook to highlight hovered or selected element
 * 
 * @param {*} param0 
 * @returns 
 */
const GLTF = ({
    url,
    id
}) => {
    const modelRef = useRef();

    const { gl, scene, camera } = useThree();
    const { t } = useTranslation();

    scene.traverse((object) => {
        if(object.isMesh === true && object.material.map !== null) {
            object.material.map.anisotropy = gl.capabilities.getMaxAnisotropy();
        }
    });

    const regenerateUsdz = useSelector((state) => state.project.regenerateUsdz);

    const urlParams = url.split('/');
    let file = urlParams.at(-1);

    const MANAGER = new THREE.LoadingManager();
    const DRACO_LOADER = new DRACOLoader(MANAGER)
        .setDecoderPath(`${THREE_PATH}/examples/jsm/libs/draco/gltf/`);
    const KTX2_LOADER = new KTX2Loader(MANAGER)
        .setTranscoderPath(`${THREE_PATH}/examples/jsm/libs/basis/`);

    const gltf = useLoader(GLTFLoader, url, async (loader) => {
        const dracoLoader = new DRACOLoader();
        const ktx2Loader = new KTX2Loader();

        dracoLoader.setDecoderPath(DRACO_LOADER);
        ktx2Loader.setTranscoderPath(KTX2_LOADER);

        loader.setDRACOLoader(dracoLoader);
        loader.setKTX2Loader(ktx2Loader);
        loader.setMeshoptDecoder(MeshoptDecoder);
    });

    async function loadTextures(scene) {
        const promises = [];

        console.log('texture loading started', promises);
    
        scene.traverse((child) => {
            if (child.isMesh && child.material.map) {
                const texture = child.material.map;

                console.log(texture);

                promises.push(new Promise((resolve) => {
                    if (texture.image) {
                        resolve();
                    } else {
                        texture.image.onload = resolve;
                    }
                }));
            }
        });

        console.log('textures loaded', promises);
    
        return Promise.all(promises);
    }

    const [selectedMesh, setSelectedMesh] = useState(null);
    // const [selectedElement, setSelectedElement] = useState(null);
    const [hoveredMesh, setHoveredMesh] = useState(null);

    // const [selectedProjectElement, setSelectedProjectElement] = useState(null);
    const [hoveredProjectElement, setHoveredProjectElement] = useState(null);

    const [originalColors, setOriginalColors] = useState([]);

    const selectedMeshId = useSelector((state) => state.project.selectedMeshId);
    const selectedProjectElementId = useSelector((state) => state.project.selectedProjectElementId);
    const isOptimizedView = useSelector((state) => state.project.isOptimizedView);

    // Save original color of all meshes to have possibility to reset after hover or click highlight
    useEffect(() => {
        if (!gltf) return;
        const colors = getNodesColors(gltf);
        setOriginalColors(colors);
        return () => { setNodesColors(gltf, colors) }; // reset colors on dispose
    }, [gltf]);

    // Highlight meshes on hover or click
    useEffect(() => {
        if (isOptimizedView) return;
        if (originalColors) {
            setNodesColors(gltf, originalColors);
        }
        if (hoveredMesh) {
            hoveredMesh.parent.children.forEach(item => {
                if (!item.userData.EBProjectElementId) {
                    if (item.material) {
                        item.material.color.set(hoveredColor);
                    }
                } else if (item.userData.EBProjectElementId === hoveredProjectElement) {
                    if (item.material && item.material._listeners) delete item.material._listeners;
                    item.material = new THREE.MeshStandardMaterial(item.material);
                    item.material.color.set(hoveredColor);
                }
            });
        }
        if (selectedMesh) {
            // if (IS_DEV) console.log(selectedMesh.parent);
            selectedMesh.parent.children.forEach(item => {
                if (
                    (!selectedProjectElementId && item.userData.EBMeshId === `${selectedMeshId}`)
                    || !item.userData.EBProjectElementId
                ) {
                    if (item.material) {
                        item.material.color.set(selectedColor);
                    }
                } else if (item.userData.EBProjectElementId == selectedProjectElementId) {
                    if (item.material && item.material._listeners) delete item.material._listeners;
                    item.material = new THREE.MeshStandardMaterial(item.material);
                    item.material.color.set(selectedColor);
                }
            });
        }
    }, [hoveredMesh, selectedMesh, selectedProjectElementId]);
    
    // mesh click listener
    const onClickListener = (event) => {
        if (isOptimizedView) return;
        if (event.intersections.length === 0) return;

        const { userData, name } = event.intersections[0].object;

        // if (IS_DEV) console.log({ name }, userData, selectedMesh);

        if (userData?.EBMeshId) {
            const { EBMeshId, EBProjectElementId } = userData;

            // unselect on click again
            if (selectedMeshId && selectedMeshId === EBMeshId) {
                if (selectedMesh && selectedMesh.name === name) {
                    setMeshId(null);
                    setProjectElementId(null);

                    setSelectedMesh(null);
                    setMesh(null);
                    
                    return;
                }
            }

            setMeshId(EBMeshId);
            setProjectElementId(EBProjectElementId);
        }

        setSelectedMesh(event.intersections[0].object);
        setMesh(event.intersections[0].object);

        event.stopPropagation();
    };

    useEffect(() => {
        const foundMeshes = findNodeByUserData(gltf, 'EBMeshId', selectedMeshId);
        const firstFound = foundMeshes.length ? foundMeshes[0] : null;
        // if (IS_DEV) console.log(firstFound?.userData, foundMeshes);
        if (firstFound !== selectedMesh) setSelectedMesh(firstFound);
    }, [selectedMeshId, gltf, selectedProjectElementId]);

    // mesh onMouseHover listener
    const onPointerOverListener = (event) => {
        if (isOptimizedView) return;
        if (event.intersections.length === 0) return;

        event.stopPropagation();

        setHoveredMesh(event.intersections[0].object);

        const { userData } = event.intersections[0].object;

        if (userData?.EBMeshId) {
            setHoveredProjectElement(userData.EBProjectElementId);
        }
    };

    // mesh onMouseLeave listener
    const onPointerOutListener = (event) => {
        setHoveredMesh(null);
    };
    
    return (
        <group ref={modelRef}>
            <primitive
                object={gltf.scene}
                onPointerOver={(event) => onPointerOverListener(event)}
                onPointerOut={(event) => onPointerOutListener(event)}
                onClick={(event) => onClickListener(event)}
            />
        </group>
    );
};

/**
 * Load project GLTF default model for detailed view and optimized model for simplified view
 * 
 * @param {*} param0 
 * @returns 
 */
const SuspenseGLTF = ({
    url,
    setPanZoomEnabled,
    id,
    usdzUrl,
    setUsdz,
    t,
    center,
    setCenter,
    setDistance,
    setScene
}) => {
    const project = useSelector((state) => state.project.selectedProject);
    const isOptimizedView = useSelector((state) => state.project.isOptimizedView);

    return url && (
        <Suspense fallback={<Loading />}>
            {isOptimizedView ? (
                <GLTF
                    key="project-optimized"
                    url={project.model.gltf.optimized}
                    setPanZoomEnabled={setPanZoomEnabled}
                    id={id}
                    usdzUrl={usdzUrl}
                    setUsdz={setUsdz}
                    t={t}
                    center={center}
                    setCenter={setCenter}
                    setDistance={setDistance}
                    setScene={setScene}
                />
            ) : (
                <GLTF
                    key="project-default"
                    url={project.model.gltf.default}
                    setPanZoomEnabled={setPanZoomEnabled}
                    id={id}
                    usdzUrl={usdzUrl}
                    setUsdz={setUsdz}
                    t={t}
                    center={center}
                    setCenter={setCenter}
                    setDistance={setDistance}
                    setScene={setScene}
                />
            )}
        </Suspense>
    );
};

/**
 * Creates screenshot of the project when it's loaded after 3 seconds
 * @param {*} param0 
 */
const AutoScreenshot = ({ timeout = 3000, id }) => {
    const { gl, scene, camera } = useThree();
    
    useEffect(() => {
        const timer = setTimeout(() => {
            if (id && gl) {
                //scene.background = null;

                gl.setClearColor(0x000000, 0); // the default
                gl.render(scene, camera);

                const data = gl.domElement.toDataURL();
            
                apiClient.post('/api/projects/save-screenshot', { id, data }).then(response => {}).catch(error => {});
            }
        }, timeout);
        return () => clearTimeout(timer);
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
};

/**
 * Project scene
 * 
 * @param {*} param0 
 * @returns 
 */
const Scene = ({
    elements,
    modelUrl,
    setPanZoomEnabled,
    id,
    usdzUrl,
    setUsdz,
    t,
    center,
    setCenter,
    setDistance,
    setScene
}) => {
    return (
        <group name="project">
            {modelUrl ? (
                <SuspenseGLTF
                    url={modelUrl}
                    elements={elements}
                    setPanZoomEnabled={setPanZoomEnabled}
                    id={id}
                    usdzUrl={usdzUrl}
                    setUsdz={setUsdz}
                    t={t}
                    center={center}
                    setCenter={setCenter}
                    setDistance={setDistance}
                    setScene={setScene}
                />
            ) : (
                <Project elements={elements} />
            )}
        </group>
    );
};

function ProjectEditorV1({abort}) {
    const { t } = useTranslation();
    
    // const [projectElements, setProjectElements] = useState(null);
    // const [isOptimizedView, setIsOptimizedView] = useState(true);
    const [model, setModel] = useState(null);
    const [usdz, setUsdz] = useState(null);
    const [viewType, setViewType] = useState('3D');
    const [stats, setStats] = useState(0);

    const [center, setCenter] = useState({ x: 0, y: 0, z: 0 });
    const [distance, setDistance] = useState(CAMERA_DISTANCE);
    // const [selectedObject, setSelectedObject] = useState(null);
    const [panZoomEnabled, setPanZoomEnabled] = useState(true);

    const [scene, setScene] = useState(false);

    const projectIsLoading = useSelector((state) => state.project.projectIsLoading);
    const selectedProjectData = useSelector((state) => state.project.selectedProject);
    const usdzUploadProgress = useSelector((state) => state.project.usdzUploadProgress);
    const usdzUrl = useSelector((state) => state.project.usdzUrl);
    const projectElements = useSelector((state) => state.project.elements);
    const isOptimizedView = useSelector((state) => state.project.isOptimizedView);

    // const projectElements = selectedProjectData?.project_elements;
    // const model = selectedProjectData?.model?.gltf;
    const id = selectedProjectData?.project?.id;

    const projectIsLoaded = !!selectedProjectData;
    const sceneIsLoaded = projectElements || model;

    const is3D = viewType === '3D';

    const componentMounted = useRef(true);
    const ref = useRef();

    useEffect(() => {
        if (usdzUrl) {
            setUsdz(usdzUrl);
        }
    }, [usdzUrl]);

    /**
     * Get model, default or optimized, in case of selected type
     * @returns 
     */
    const getModel = () => {
        if (!selectedProjectData.model.gltf) {
            return null;
        }

        if (isOptimizedView) {
            return selectedProjectData.model.gltf.optimized;
        }

        return selectedProjectData.model.gltf.default;
    };

    const getUsdz = () => {
        if (!selectedProjectData.model.usdz) {
            return null;
        }

        return selectedProjectData.model.usdz;
    };

    /**
     * Update scene asynchronously
     */
    const updateAsync = async () => {
        if (componentMounted.current) {
            if (selectedProjectData !== null) {
                if (!getModel()) {
                    setElements(selectedProjectData.project_elements);
                } else {
                    loadAndSetProjectElements();
                }

                setModel(getModel());
                setUsdz(getUsdz());

                // const size = convertSize(selectedProjectData.project);
                // const center = calcProjectCenter(size);
                
                const center = calcProjectElementsCenter(selectedProjectData, getModel());
                setCenter(center);
                
                // const distance = calcCameraDistance(size);
                const distance = calcCameraDistance(center, true, 25);
                setDistance(distance);
            } else {
                setModel(null);
                setUsdz(null);

                setCenter(calcProjectCenter({}));
                setDistance(CAMERA_DISTANCE);
            }
        }
    };

    /**
     * Load project elements
     */
    const loadAndSetProjectElements = () => {
        apiClient.post('/api/projects/load-project-elements', { id: Number(id) }).then((response) => {
            if (!response.data.success) return;
            setElements(response.data.project_elements);
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t('An error occurred while loading project elements'), 'error'));
        });
    };

    /**
     * useEffect hook
     * If selectedProjectData changed then update scene
     */
    useEffect(() => {
        componentMounted.current = true;
        updateAsync();
        return () => { componentMounted.current = false };
    }, [selectedProjectData]); // eslint-disable-line react-hooks/exhaustive-deps

    const style = {
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        flexGrow: 1,
        width: '100',
    };

    /**
     * Change optimized view type listener
     */
    const handleOptimizedViewChange = () => {
        setOptimizedView(!isOptimizedView);
    };

    function saveScreenshot() {
        const data = ref.current.toDataURL('image/png');
        
        apiClient.post('/api/projects/save-screenshot', { id, data })
            .then(response => {
                if(response.data.success) {
                    store.dispatch(toggleMessageOpen(t('Screenshot saved successfully!'), 'success'));
                } else {
                    store.dispatch(toggleMessageOpen(t('An error occurred while saving screenshot!'), 'error'));
                }
            })
            .catch(error => {
                store.dispatch(toggleMessageOpen(t('An error occurred while saving screenshot!'), 'error'))
            });
    }
    
    return (
        <div style={style}>
            <Canvas
                shadows={SHADOWS}
                gl={{ preserveDrawingBuffer: true, precision: 'lowp' }}
                style={{ cursor: ! isOptimizedView ? 'pointer' : 'default' }}
                ref={ref}
            >
                <ThemedBackground />
                <Lights shadows={SHADOWS} />
                <Cameras perspective={is3D} position={{ ...center, z: distance }} />
                <Controls rotate={is3D} target={{ ...center, z: 0 }} enabled={panZoomEnabled} />
                {is3D && <ThemedFloor />}
                {!abort && sceneIsLoaded &&
                    <Scene
                        id={id}
                        elements={projectElements}
                        modelUrl={model}
                        usdzUrl={usdz}
                        setPanZoomEnabled={setPanZoomEnabled}
                        setUsdz={setUsdz}
                        t={t}
                        center={center}
                        setCenter={setCenter}
                        setDistance={setDistance}
                        setScene={setScene}
                    />
                }
                {projectIsLoaded && !selectedProjectData.project.screenshot  && <AutoScreenshot id={id} /> }
            </Canvas>
            {/*projectIsLoaded && <ManualScreenShot />*/}
            {projectIsLoaded && <IconButton onClick={() => saveScreenshot(id, ref)} style={{position: 'absolute', bottom: '5px', right: '5px'}} color='primary'><CameraAlt /></IconButton>}
            {!projectIsLoading && !projectIsLoaded && <LoadingScreen text="No project selected" noProgress />}
            {projectIsLoading && <LoadingScreen text="Downloading Project" />}
            {projectIsLoaded &&
                <FloatingButtons
                    viewType={viewType}
                    setViewType={setViewType}
                    isOptimizedView={isOptimizedView}
                    handleOptimizedViewChange={handleOptimizedViewChange}
                    model={model}
                    id={id}
                    usdzUrl={usdz}
                />
            }
            {/*STATS && <Stats className="three-stats" showPanel={stats} onClick={() => setStats(v=>v<2?v+1:0)} />*/}
        </div>
    );
}

export default ProjectEditorV1;
